<script setup>
import { Handle, Position } from '@vue-flow/core';
import { NodeToolbar } from '@vue-flow/node-toolbar';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { triggerNodeAction } from '../nodeEventBus';

const props = defineProps({
  id: { type: String, default: '' },
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const isHovered = ref(false);
let hoverTimeout = null;
const onMouseEnter = () => {
  clearTimeout(hoverTimeout);
  isHovered.value = true;
};
const onMouseLeave = () => {
  hoverTimeout = setTimeout(() => {
    isHovered.value = false;
  }, 300);
};

const eventLabel = eventName => {
  if (!eventName) return t('FLOW_BUILDER.NODE_EDITOR.TRIGGER.EVENT');
  const key = `FLOW_BUILDER.NODE_EDITOR.TRIGGER.EVENTS.${eventName.toUpperCase()}`;
  const translated = t(key);
  return translated === key ? eventName : translated;
};
</script>

<template>
  <div
    class="flow-node min-w-[180px] bg-white dark:bg-n-slate-1 rounded-[10px] p-2 text-[10px] shadow-md hover:shadow-lg border transition-all relative"
    :class="
      selected
        ? 'border border-green-500 shadow-lg'
        : 'border border-transparent hover:border-n-blue-8'
    "
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <NodeToolbar :is-visible="isHovered" :position="Position.Top" :offset="8">
      <div
        class="flex gap-1 bg-white dark:bg-n-slate-2 rounded-lg shadow-lg p-1"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-blue-1 dark:hover:bg-n-blue-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DUPLICATE')"
          @click.stop="triggerNodeAction('duplicate', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
          </svg>
        </button>
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-ruby-1 dark:hover:bg-n-ruby-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DELETE')"
          @click.stop="triggerNodeAction('delete', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
        </button>
      </div>
    </NodeToolbar>
    <div class="flex items-center gap-2">
      <span class="i-lucide-zap text-[10px] text-n-blue-10" />
      <div class="flex flex-col">
        <span class="font-semibold text-n-slate-12 text-[9px]">
          {{ t('FLOW_BUILDER.SIDEBAR.TRIGGER') }}
        </span>
        <span class="text-[8px] text-n-slate-11">
          {{ eventLabel(props.data?.event_name) }}
        </span>
        <span
          v-if="props.data?.inbox_name"
          class="text-[8px] text-n-blue-10 font-medium"
        >
          {{ props.data.inbox_name }}
        </span>
      </div>
    </div>
    <Handle
      type="source"
      :position="Position.Right"
      class="!w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.HANDLE_OUTPUT')"
    />
  </div>
</template>
